import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit2, Trash2, FileText, Package2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getProposalTemplates, deleteProposalTemplate, ProposalTemplate } from '../lib/proposalTemplates';
import toast from 'react-hot-toast';

interface ProposalTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate?: (template: ProposalTemplate) => void;
  onCreateTemplate?: () => void;
  onEditTemplate?: (template: ProposalTemplate) => void;
  refreshKey?: number;
}

export default function ProposalTemplatesModal({
  isOpen,
  onClose,
  onSelectTemplate,
  onCreateTemplate,
  onEditTemplate,
  refreshKey,
}: ProposalTemplatesModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadTemplates();
    }
  }, [isOpen, selectedBusiness?.id, refreshKey]);

  const loadTemplates = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoading(true);
    try {
      const data = await getProposalTemplates(selectedBusiness.id);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading proposal templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await deleteProposalTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template: ProposalTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    onEditTemplate?.(template);
  };

  if (!isOpen && !isAnimating) return null;

  const summarizeTemplate = (template: ProposalTemplate) => {
    const offers = template.template_data?.offers || [];
    const totalServices = offers.reduce((sum, o) => sum + (o.services?.length || 0), 0);
    const totalProducts = offers.reduce((sum, o) => sum + (o.products?.length || 0), 0);
    const parts: string[] = [];
    if (offers.length) parts.push(`${offers.length} offer${offers.length !== 1 ? 's' : ''}`);
    if (totalServices) parts.push(`${totalServices} service${totalServices !== 1 ? 's' : ''}`);
    if (totalProducts) parts.push(`${totalProducts} product${totalProducts !== 1 ? 's' : ''}`);
    return parts.join(' · ') || 'Empty template';
  };

  return createPortal(
    <div
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100160]`}
      onClick={onClose}
    >
      <div
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl z-[100160] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header — iOS style: white, icon tile + title/subtitle, round close */}
          <div className="bg-white border-b border-gray-100" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-sm">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-[17px] font-bold text-gray-900 leading-tight" style={{ letterSpacing: '-0.01em' }}>
                    Proposal Templates
                  </h2>
                  <p className="text-[12px] text-gray-500 truncate">Reusable offers for faster proposals</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600 mx-auto mb-4" />
                  <p className="text-sm font-medium text-gray-500">Loading templates...</p>
                </div>
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-6 py-12">
                <div className="text-center max-w-md">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50">
                    <FileText className="h-8 w-8 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>No templates yet</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-6">
                    Create your first proposal template to quickly build proposals with pre-filled offers and services.
                  </p>
                  <button
                    onClick={() => onCreateTemplate?.()}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all duration-150 touch-manipulation shadow-sm"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4" />
                    Create First Template
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 sm:p-5 space-y-2.5">
                <button
                  onClick={() => onCreateTemplate?.()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-gray-200 bg-white/60 text-sm font-semibold text-blue-600 hover:border-blue-300 hover:bg-blue-50/50 active:scale-[0.99] transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4" />
                  New Template
                </button>

                {templates.map(template => (
                  <div
                    key={template.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onSelectTemplate?.(template);
                      onClose();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectTemplate?.(template);
                        onClose();
                      }
                    }}
                    className="bg-white border border-gray-200/70 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] active:bg-gray-50 hover:border-blue-300 transition-all duration-150 cursor-pointer touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[15px] font-semibold text-gray-900 mb-1" style={{ letterSpacing: '-0.01em' }}>
                          {template.name}
                        </h3>
                        <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
                          <Package2 className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{summarizeTemplate(template)}</span>
                        </div>
                        {template.template_data?.title && (
                          <p className="text-[12px] text-gray-400 mt-1 truncate">
                            Default title: {template.template_data.title}
                          </p>
                        )}
                      </div>
                      {/* Always visible (touch devices have no hover) */}
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={e => handleEdit(template, e)}
                          className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 active:scale-95 rounded-full transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          title="Edit template"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={e => handleDelete(template.id, e)}
                          className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 active:scale-95 rounded-full transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
